import { useCallback } from "react";
import { api } from "../utils/apiHandler";
import type {
  SignInRequest,
  SignInResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  RefreshRequest,
  RefreshResponse,
} from "./types/authTypes";
import type { AxiosResponse } from "axios";

const AUTH_PATH = "/auth";

const useAuthResource = () => {
  // Sign in (request OTP)
  const signIn = useCallback(
    async (params: SignInRequest): Promise<SignInResponse> => {
      const response: AxiosResponse<SignInResponse> = await api.request<
        SignInRequest,
        AxiosResponse<SignInResponse>
      >({
        method: "POST",
        url: `${AUTH_PATH}/sign_in`,
        data: params,
      });
      return response.data;
    },
    []
  );

  // Verify OTP
  const verifyOtp = useCallback(
    async (params: VerifyOtpRequest): Promise<VerifyOtpResponse> => {
      const response: AxiosResponse<VerifyOtpResponse> = await api.request<
        VerifyOtpRequest,
        AxiosResponse<VerifyOtpResponse>
      >({
        method: "POST",
        url: `${AUTH_PATH}/verify_otp`,
        data: params,
      });
      return response.data;
    },
    []
  );

  // Refresh token
  const refresh = useCallback(
    async (params: RefreshRequest): Promise<RefreshResponse> => {
      const response: AxiosResponse<RefreshResponse> = await api.request<
        RefreshRequest,
        AxiosResponse<RefreshResponse>
      >({
        method: "PATCH",
        url: `${AUTH_PATH}/refresh`,
        data: params,
      });
      return response.data;
    },
    []
  );

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    await api.request({
      method: "DELETE",
      url: `${AUTH_PATH}/sign_out`,
    });
  }, []);

  return {
    signIn,
    verifyOtp,
    refresh,
    signOut,
  };
};

// Export a plain refresh function for non-hook usage
export async function refresh(
  params: RefreshRequest
): Promise<RefreshResponse> {
  const response: import("axios").AxiosResponse<RefreshResponse> =
    await api.request<
      RefreshRequest,
      import("axios").AxiosResponse<RefreshResponse>
    >({
      method: "PATCH",
      url: `${AUTH_PATH}/refresh`,
      data: params,
    });
  return response.data;
}

export default useAuthResource;
